import { useMemo, useReducer } from 'react';
import type { GroupFormData } from './GroupFormConfig';
import { defaultGroupFormValues } from './GroupFormConfig';

export type FormMode = 'create' | 'edit';
// State interface
export interface GroupFormState {
    formData: GroupFormData;
    mode: FormMode;
    isSubmitting: boolean;
}

// Actions interface
export interface GroupFormActions {
    updateFormData: (data: Partial<GroupFormData>) => void;
    submit: (data: Partial<GroupFormData>) => void;
    submitStart: () => void;
    submitSuccess: () => void;
    submitError: () => void;
    reset: () => void;
    initialize: (data: Partial<GroupFormData>) => void;
}

type GroupFormAction =
    | { type: 'UPDATE_FORM_DATA'; payload: Partial<GroupFormData> }
    | { type: 'SUBMIT'; payload: Partial<GroupFormData> }
    | { type: 'SUBMIT_START' }
    | { type: 'SUBMIT_SUCCESS' }
    | { type: 'SUBMIT_ERROR' }
    | { type: 'RESET' }
    | { type: 'INITIALIZE'; payload: Partial<GroupFormData> };

function groupFormReducer(state: GroupFormState, action: GroupFormAction): GroupFormState {
    switch (action.type) {
        case 'UPDATE_FORM_DATA':
            return {
                ...state,
                formData: { ...state.formData, ...action.payload },
            };
        case 'SUBMIT':
            return {
                ...state,
                isSubmitting: true,
            };
        case 'SUBMIT_START':
            return {
                ...state,
                isSubmitting: true,
            };
        case 'SUBMIT_SUCCESS':
        case 'SUBMIT_ERROR':
            return {
                ...state,
                isSubmitting: false,
            };
        case 'RESET':
            return {
                ...state,
                formData: defaultGroupFormValues,
                isSubmitting: false,
            };
        case 'INITIALIZE':
            return {
                ...state,
                formData: { ...defaultGroupFormValues, ...action.payload },
            };
        default:
            return state;
    }
}

export function useGroupFormState(mode: FormMode, initialData?: Partial<GroupFormData>) {
    // Initialize state
    const initialState: GroupFormState = {
        formData: initialData ? { ...defaultGroupFormValues, ...initialData } : defaultGroupFormValues,
        mode,
        isSubmitting: false,
    };

    // Create reducer
    const [state, dispatch] = useReducer(groupFormReducer, initialState);
    const actions = useMemo<GroupFormActions>(
        () => ({
            updateFormData: data => dispatch({ type: 'UPDATE_FORM_DATA', payload: data }),
            submit: data => dispatch({ type: 'SUBMIT', payload: data }),
            submitStart: () => dispatch({ type: 'SUBMIT_START' }),
            submitSuccess: () => dispatch({ type: 'SUBMIT_SUCCESS' }),
            submitError: () => dispatch({ type: 'SUBMIT_ERROR' }),
            reset: () => dispatch({ type: 'RESET' }),
            initialize: data => dispatch({ type: 'INITIALIZE', payload: data }),
        }),
        [dispatch]
    );

    return { state, actions };
}
